/**
 * Section D Page - D.影響評估
 * Environmental and social impacts and sustainable development impacts
 * UI 模式：2 主頁籤 + 子頁籤
 *   D.1. Article 6.4 SD Tool（D.1.1 / D.1.2 / D.1.3）
 *   D.2. Host Party Regulations（D.2.1 / D.2.2）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import sectionDService, {
  SectionDDetail, SectionDFieldKey,
} from '../services/sectionDService';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── TextTab ───────────────────────────────────────────────────────
interface TextTabProps {
  projectId: number;
  fieldKey: SectionDFieldKey;
  initialValue: string | null;
  canEdit: boolean;
  hint?: string;
  onSaved: (d: SectionDDetail) => void;
}

const TextTab: React.FC<TextTabProps> = ({ projectId, fieldKey, initialValue, canEdit, hint, onSaved }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [value, setValue] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(initialValue || ''); }, [initialValue]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionDService.saveSectionD(projectId, { [fieldKey]: value });
      show(true, t('sectionD.saveSuccess'));
      onSaved(res);
    } catch {
      show(false, t('sectionD.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {hint && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#e6f7ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 13, color: '#0958d9' }}>
          ℹ {hint}
        </div>
      )}
      <RichTextEditor value={value} onChange={setValue} disabled={!canEdit} minHeight="200px" />
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 24px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#16a085', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {saving ? t('sectionD.saving') : t('sectionD.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Main Page
// ════════════════════════════════════════════════════════════════
type MainTab = 'd1' | 'd2';
type SubTab  = 'd11' | 'd12' | 'd13' | 'd21' | 'd22';

const SectionDPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('section_d', 'update');

  const [detail, setDetail] = useState<SectionDDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [mainTab, setMainTab] = useState<MainTab>('d1');
  const [subTab, setSubTab] = useState<SubTab>('d11');
  const [resetConfirm, setResetConfirm] = useState(false);

  // ── Load ──────────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionDPage');
      const res = await sectionDService.getSectionDByProject(projectId);
      setDetail(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── Reset ─────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionDService.resetSectionD(selectedProjectId);
      setDetail(null);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── Tab styles ────────────────────────────────────────────────
  const mainTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', fontSize: 14, fontWeight: active ? 700 : 500,
    background: active ? '#16a085' : '#f5f5f5',
    color: active ? '#fff' : '#555',
    cursor: 'pointer',
    borderTop: '1px solid #e0e0e0',
    borderLeft: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    borderBottom: active ? '2px solid #fff' : '1px solid #e0e0e0',
    borderRadius: '6px 6px 0 0',
    marginRight: 4, transition: 'all 0.15s',
  });

  const subTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 18px', fontSize: 13, fontWeight: active ? 700 : 400,
    borderTop: 'none', borderLeft: 'none', borderRight: 'none',
    borderBottom: `3px solid ${active ? '#16a085' : 'transparent'}`,
    color: active ? '#16a085' : '#555',
    cursor: 'pointer', background: 'none',
    transition: 'all 0.15s',
  });

  // sub-tab to fieldKey mapping
  const fieldMap: Record<SubTab, SectionDFieldKey> = {
    d11: 'env_social_risk_summary',
    d12: 'sd_impacts_summary',
    d13: 'sd_monitoring_plan',
    d21: 'host_party_requirements',
    d22: 'assessment_conclusion',
  };

  // hint messages
  const hintMap: Record<SubTab, string | undefined> = {
    d11: isZh
      ? '完整的環境與社會保障風險評估表及管理計畫表請附於附件六及附件七。[Attach the completed forms as Appendixes 6 and 7]'
      : 'Attach the A6.4 Environmental and Social Safeguards Risk Assessment Form (Appendix 6) and the Environmental and Social Management Plan Form (Appendix 7).',
    d12: isZh
      ? '完整的永續發展影響表請附於附件八。[Attach the completed form as Appendix 8]'
      : 'Attach the A6.4 Sustainable Development Impact Form as Appendix 8.',
    d13: isZh
      ? '完整表格請附於附件六、七、八。[Attach the completed forms in Appendixes 6, 7 and 8]'
      : 'Attach the completed forms in Appendixes 6, 7 and 8.',
    d21: undefined,
    d22: undefined,
  };

  const currentSubTab = mainTab === 'd1'
    ? (['d11', 'd12', 'd13'] as SubTab[])
    : (['d21', 'd22'] as SubTab[]);

  // Ensure subTab matches the active mainTab
  const activeSubTab: SubTab = currentSubTab.includes(subTab) ? subTab : currentSubTab[0];

  const handleMainTab = (tab: MainTab) => {
    setMainTab(tab);
    setSubTab(tab === 'd1' ? 'd11' : 'd21');
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_d" />
        {canUpdate && detail && (
          <button onClick={() => setResetConfirm(true)}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
            🗑 {t('sectionD.resetData')}
          </button>
        )}
      </div>

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>{t('sectionD.selectProject')}</div>
      ) : loading ? (
        <div className="loading">{t('sectionD.loading')}</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>

          {/* ── Main Tabs ── */}
          <div style={{ display: 'flex', padding: '16px 20px 0', borderBottom: '1px solid #e0e0e0', background: '#fafafa', borderRadius: '8px 8px 0 0' }}>
            <button style={mainTabStyle(mainTab === 'd1')} onClick={() => handleMainTab('d1')}>
              {t('sectionD.tabD1')}
            </button>
            <button style={mainTabStyle(mainTab === 'd2')} onClick={() => handleMainTab('d2')}>
              {t('sectionD.tabD2')}
            </button>
          </div>

          {/* ── Sub Tabs ── */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', padding: '0 20px', gap: 4 }}>
            {mainTab === 'd1' && (
              <>
                <button style={subTabStyle(activeSubTab === 'd11')} onClick={() => setSubTab('d11')}>{t('sectionD.tabD11')}</button>
                <button style={subTabStyle(activeSubTab === 'd12')} onClick={() => setSubTab('d12')}>{t('sectionD.tabD12')}</button>
                <button style={subTabStyle(activeSubTab === 'd13')} onClick={() => setSubTab('d13')}>{t('sectionD.tabD13')}</button>
              </>
            )}
            {mainTab === 'd2' && (
              <>
                <button style={subTabStyle(activeSubTab === 'd21')} onClick={() => setSubTab('d21')}>{t('sectionD.tabD21')}</button>
                <button style={subTabStyle(activeSubTab === 'd22')} onClick={() => setSubTab('d22')}>{t('sectionD.tabD22')}</button>
              </>
            )}
          </div>

          {/* ── Content ── */}
          <div style={{ padding: '20px 24px' }}>
            <TextTab
              key={activeSubTab}
              projectId={selectedProjectId!}
              fieldKey={fieldMap[activeSubTab]}
              initialValue={detail?.[fieldMap[activeSubTab]] ?? null}
              canEdit={canUpdate}
              hint={hintMap[activeSubTab]}
              onSaved={setDetail}
            />
          </div>
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionD.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionD.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)} style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>{t('sectionD.cancel')}</button>
              <button onClick={handleReset} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>{t('sectionD.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionDPage;
